const { fetchJSON, getDefaultRequestConfig, getElement } = window.BAO.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = ['/apps/', '/a/', '/tools/', '/community/']

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return '/'
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections.join(','))
    formData.append('sections_url', sectionsUrl)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    const variantId = formData.get('id')

    try {
      let state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      // Handle GWP?
      console.info(state, 'add state');
      const sectionsData = state.sections
      const fullCartState = await this.getCart()
      console.info(fullCartState, 'cartContents');
      fullCartState.sections = sectionsData
      state = fullCartState

      state = await this.processGiftWithPurchase(state)

      // Move the bundle parent removal logic here
      const bundleParent = form.getAttribute('data-product-remove')
      if (bundleParent) {
        state = await window.BAO.Cart.removeItem(
          bundleParent,
          {
            sections,
          },
        )
        console.info('Removing bundle parent:', bundleParent)
      }

      console.info('addItemFromForm state:', state)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
        {
          form,
          formData,
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err, variantId)
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
      silent = false,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    if (!silent) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
        items,
        attributes,
        form,
      })
    }

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)
      console.info('addItem state:', state)

      if (!silent) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
          items,
          state,
          form,
        })

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })
      }

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartChange(body)
      console.info('quantity', state);
      state = await this.processFreeSamples(state)
      state = await this.processGiftWithPurchase(state)

      console.info('updateQuantity state:', state)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
      // Return current cart state on error instead of undefined
      return await this.getCart()
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      silent = false,
    } = {},
  ) {
    // Get the current cart state
    const currentState = await this.getCart()
    // Find all non-sample items
    const nonSampleItems = currentState.items.filter(item => !item.properties?._freeSample)
    // Find the free sample item
    const freeSampleItem = currentState.items.find(item => item.properties?._freeSample)
    // Check if removing this item will leave only the sample
    const isRemovingLastNonSample = nonSampleItems.length === 1 && nonSampleItems[0].key === key

    try {
      let state

      // If removing the product would leave a free sample alone, remove the sample first
      if (isRemovingLastNonSample && freeSampleItem) {
        state = await this.cartChange({
          id: freeSampleItem.key,
          quantity: 0,
          sections,
          sections_url: sectionsUrl,
        })

        await new Promise(resolve => setTimeout(resolve, 300))

        state = await this.getCart()
      }

      state = await this.cartChange({
        id: key,
        quantity: 0,
        sections,
        sections_url: sectionsUrl,
      })

      if (!silent) {
        state = await this.processFreeSamples(state)
        state = await this.processGiftWithPurchase(state)

        window.BAO.dispatchEvent(
          window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
          {
            state,
          },
        )

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })
      }

      return state
    } catch (err) {
      console.error('Error in removeItem:', err)
      // Return current cart state on error instead of undefined
      return await this.getCart()
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    const body = {
      updates: Object.fromEntries(keys.map(k => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartUpdate(body)
      state = await this.processFreeSamples(state)
      state = await this.processGiftWithPurchase(state)
      console.info('removeItems state:', state)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)

      return await this.getCart()
    }
  }

  /**
   * Process Gift with Purchase logic
   * Called after cart operations to add/remove gifts based on thresholds
   */
  async processGiftWithPurchase (state) {
    try {
      // Get instructions from GWP manager (doesn't mutate cart)
      const instructions = window.BAO.GiftWithPurchase.process(state)

      // If no changes needed, return state as-is
      if (instructions.toAdd.length === 0 && instructions.toRemove.length === 0) {
        return state
      }

      const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
      const sectionsUrl = DEFAULT_SECTIONS_URL

      // Remove gifts that should no longer be in cart
      for (const key of instructions.toRemove) {
        console.info('GWP: Removing gift', key)
        state = await this.cartChange({
          id: key,
          quantity: 0,
          sections,
          sections_url: sectionsUrl,
        })
      }

      // Add gifts that should be in cart
      for (const item of instructions.toAdd) {
        console.info('GWP: Adding gift', item)
        state = await this.addItem(item, {
          sections,
          sectionsUrl,
          silent: true,
        })
      }

      // Dispatch GWP state change event for UI updates
      const gwpState = window.BAO.GiftWithPurchase.getState(state)
      window.BAO.dispatchEvent('gwp:stateChanged', gwpState)

      return state
    } catch (err) {
      console.error('GWP: Processing failed', err)
      return state
    }
  }

  async processFreeSamples (state, { silent = true, noRefresh = false } = {}) {
    const sampleLimit = window.theme.freeSampleThreshold

    const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
    const sectionsUrl = DEFAULT_SECTIONS_URL

    if (sampleLimit > state.original_total_price) {
      const item = state.items.find(item =>
        item.properties._freeSample === 'true',
      )

      if (item) {
        state = await window.BAO.Cart.removeItem(item.key, { sections, sectionsUrl, silent })
      }
    }

    return state
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res, variantId = null) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res, variantId)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  async getCart () {
    return fetchJSON('/cart.js', {
      ...getDefaultRequestConfig(),
    })
  }

  /**
   * Refreshes the cart sections after an external script has added an item to the cart
   *
   * @returns {Promise<Object>} The updated cart state
   */
  async refreshCart () {
    const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
    const sectionsUrl = DEFAULT_SECTIONS_URL

    try {
      // Call section rendering to get trhe new html
      const getSections = await fetch(`${sectionsUrl}?sections=${sections.join(',')}`)
      const sectionsHtml = await getSections.json()
      const state = await this.getCart()
      state.sections = sectionsHtml

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      console.info('Cart refreshed:', state)
      return state
    } catch (err) {
      console.error('Error refreshing cart:', err)
    }
  }
}

window.BAO.Cart = new BaoCart()
